/*
 * GSA Hero carousel
 * - Crossfades between brand slides; each slide replays its story beat (CSS, .is-playing) when shown.
 * - Auto-advance pauses on keyboard focus, when the tab is hidden, and via the pause button (not on mouse hover).
 * - Reduced-motion visitors get no auto-advance and no animation (handled in CSS).
 * - In the theme editor, selecting a slide's block shows that slide and holds it.
 */
if (!customElements.get('gsa-hero-carousel')) {
  class GsaHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.gsa-hc__slide')];
      this.tabs = [...this.querySelectorAll('.gsa-hc__tab')];
      this.pauseButton = this.querySelector('[data-pause]');
      this.duration = parseInt(this.dataset.duration, 10) || 7000;
      this.index = 0;
      this.timer = null;
      this.holds = new Set();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const designMode = window.Shopify && window.Shopify.designMode;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !reduceMotion && !designMode;
      if (!this.autoplay && this.pauseButton) this.pauseButton.hidden = true;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.show(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.show(this.index + 1));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.show(Number(tab.dataset.index))));
      this.pauseButton?.addEventListener('click', () => this.togglePause());

      const viewport = this.querySelector('.gsa-hc__viewport');
      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.show(this.index + 1);
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
      });
      this.addEventListener('focusin', (event) => {
        if (event.target.matches(':focus-visible')) this.hold('focus');
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.release('focus');
      });
      this.onVisibility = () => (document.hidden ? this.hold('hidden') : this.release('hidden'));
      document.addEventListener('visibilitychange', this.onVisibility);
      this.addSwipe(viewport);

      // Theme editor: jump to the selected block's slide and hold it there.
      this.onBlockSelect = (event) => {
        const i = this.slides.findIndex((slide) => slide.contains(event.target));
        if (i < 0) return;
        this.hold('editor');
        this.show(i);
      };
      this.onBlockDeselect = () => this.release('editor');
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.show(0, { replay: false });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    show(n, { replay = true } = {}) {
      const count = this.slides.length;
      this.index = ((n % count) + count) % count;

      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        if (active && replay) {
          slide.classList.remove('is-playing');
          void slide.offsetWidth; // restart the CSS story beat
          slide.classList.add('is-playing');
        }
      });

      this.tabs.forEach((tab, i) => {
        tab.classList.toggle('is-done', i < this.index);
        tab.classList.remove('is-active');
        if (i === this.index) {
          void tab.offsetWidth; // restart the progress bar
          tab.classList.add('is-active');
          tab.setAttribute('aria-current', 'true');
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      this.schedule(this.duration);
    }

    schedule(ms) {
      clearTimeout(this.timer);
      const running = this.autoplay && this.holds.size === 0;
      this.toggleAttribute('data-running', this.autoplay);
      this.toggleAttribute('data-paused', this.autoplay && !running);
      if (!running) return;
      this.remaining = ms;
      this.startedAt = performance.now();
      this.timer = setTimeout(() => this.show(this.index + 1), ms);
    }

    hold(reason) {
      if (!this.autoplay || this.holds.has(reason)) return;
      if (this.holds.size === 0 && this.startedAt) {
        clearTimeout(this.timer);
        this.remaining = Math.max(this.remaining - (performance.now() - this.startedAt), 0);
      }
      this.holds.add(reason);
      this.toggleAttribute('data-paused', true);
    }

    release(reason) {
      if (!this.holds.delete(reason) || this.holds.size > 0) return;
      this.toggleAttribute('data-paused', false);
      this.schedule(Math.max(this.remaining ?? this.duration, 600));
    }

    togglePause() {
      const paused = this.holds.has('button');
      if (paused) this.release('button');
      else this.hold('button');
      this.pauseButton.setAttribute('aria-pressed', String(!paused));
      this.pauseButton.setAttribute('aria-label', paused ? 'Pause slideshow' : 'Play slideshow');
    }

    addSwipe(el) {
      let startX = null;
      let startY = null;
      el.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        startX = event.clientX;
        startY = event.clientY;
      });
      el.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
      });
      el.addEventListener('pointercancel', () => (startX = null));
    }
  }

  customElements.define('gsa-hero-carousel', GsaHeroCarousel);
}
